import React, { useMemo } from "react";
import { dateValue, mapNameOptions, SectionTitle } from "../EmployeeForm";
import { Label } from "@radix-ui/react-label";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { toIST } from "../../../utils/calculateMargin";
import { InputText } from "primereact/inputtext";
import { useSelector } from "react-redux";

function Employment({ formData, setFormData, handleOnChange }) {
  const { department, employeeStatus, employmentType, workLocation } =
    useSelector((state) => state.masterData);
  const departmentOptions = useMemo(
    () => mapNameOptions(department),
    [department],
  );
  const employeeStatusOptions = useMemo(
    () => mapNameOptions(employeeStatus),
    [employeeStatus],
  );
  const employmentTypeOptions = useMemo(
    () => mapNameOptions(employmentType),
    [employmentType],
  );
  const workLocationOptions = useMemo(
    () => mapNameOptions(workLocation),
    [workLocation],
  );

  const companyNameOptions = ["Microworld Infosol Private Limited", "Dayma Infotech"].map((x) => ({
    label: x,
    value: x,
  }));
  const calculateProbation = (dojDate, confirmationDate) => {
    const doj = new Date(dojDate);
    const confirmation = new Date(confirmationDate);
    confirmation.setDate(confirmation.getDate() - 1);

    let months =
      (confirmation.getFullYear() - doj.getFullYear()) * 12 +
      (confirmation.getMonth() - doj.getMonth());

    let tempDate = new Date(doj);
    tempDate.setMonth(tempDate.getMonth() + months);

    if (tempDate > confirmation) {
      months--;
      tempDate = new Date(doj);
      tempDate.setMonth(tempDate.getMonth() + months);
    }

    const diffTime = confirmation - tempDate;
    let days = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;

    if (
      days ===
      new Date(tempDate.getFullYear(), tempDate.getMonth() + 1, 0).getDate()
    ) {
      months++;
      days = 0;
    }

    let result = "";
    if (months > 0) result += `${months} month${months > 1 ? "s" : ""}`;
    if (days > 0)
      result += `${months > 0 ? " " : ""}${days} day${days > 1 ? "s" : ""}`;
    return result || "0 days";
  };

  const calculateNoticePeriod = (resignationDate, releavingDate) => {
    if (!resignationDate || !releavingDate) return "";

    const resignation = new Date(resignationDate);
    const releaving = new Date(releavingDate);

    const diffTime = releaving - resignation;

    if (diffTime < 0) return "";

    const days = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return days.toString();
  };
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Employment</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 w-full">
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Department <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.department}
            options={departmentOptions}
            onChange={(e) => handleOnChange("department", e.value)}
            placeholder="Select Department"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Employee Status <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.employeeStatus}
            options={employeeStatusOptions}
            onChange={(e) => handleOnChange("employeeStatus", e.value)}
            placeholder="Select Status"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Employment Type <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.employmentType}
            options={employmentTypeOptions}
            onChange={(e) => handleOnChange("employmentType", e.value)}
            placeholder="Select Type"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Company Name <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.companyName}
            options={companyNameOptions}
            onChange={(e) => handleOnChange("companyName", e.value)}
            placeholder="Select Location"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Work Location <span className="text-red-600">*</span>
          </Label>
          <Dropdown
            value={formData.workLocation}
            options={workLocationOptions}
            onChange={(e) => handleOnChange("workLocation", e.value)}
            placeholder="Select Location"
            filter
            className="w-full px-1 text-sm border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 w-full">
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Date of Joining <span className="text-red-600">*</span>
          </Label>
          <Calendar
            value={dateValue(formData.dateOfJoining)}
            onChange={(e) => {
              const istDate = toIST(e.value);
              const probation =
                istDate && formData.dateOfConfirmation
                  ? calculateProbation(istDate, formData.dateOfConfirmation)
                  : "";
              setFormData((prev) => ({
                ...prev,
                dateOfJoining: istDate,
                probation,
              }));
            }}
            showIcon
            dateFormat="dd/mm/yy"
            placeholder="Select DOJ"
            className="w-full bg-input border border-gray-300 rounded-md"
            inputClassName="w-full px-3 py-1.5 bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">
            Date of Confirmation <span className="text-red-600">*</span>
          </Label>
          <Calendar
            value={dateValue(formData.dateOfConfirmation)}
            onChange={(e) => {
              const istDate = toIST(e.value);
              const probation =
                formData.dateOfJoining && istDate
                  ? calculateProbation(formData.dateOfJoining, istDate)
                  : "";
              setFormData((prev) => ({
                ...prev,
                dateOfConfirmation: istDate,
                probation,
              }));
            }}
            showIcon
            dateFormat="dd/mm/yy"
            placeholder="Select Confirmation"
            className="w-full bg-input border border-gray-300 rounded-md"
            inputClassName="w-full px-3 py-1.5 bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">Probation</Label>
          <InputText
            value={formData.probation}
            readOnly
            placeholder="Auto calculated"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 w-full">
        <div className="flex flex-col gap-1">
          <Label className="px-3">Date of Resignation</Label>
          <Calendar
            value={dateValue(formData.dateOfResignation)}
            onChange={(e) => {
              const resignationDate = toIST(e.value);

              const noticePeriod =
                resignationDate && formData.dateOfReleaving
                  ? calculateNoticePeriod(
                      resignationDate,
                      formData.dateOfReleaving,
                    )
                  : "";

              setFormData((prev) => ({
                ...prev,
                dateOfResignation: resignationDate,
                noticePeriod,
              }));
            }}
            showIcon
            dateFormat="dd/mm/yy"
            placeholder="Select Resignation"
            className="w-full bg-input border border-gray-300 rounded-md"
            inputClassName="w-full px-3 py-1.5 bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">Date of Releaving</Label>
          <Calendar
            value={dateValue(formData.dateOfReleaving)}
            onChange={(e) => {
              const releavingDate = toIST(e.value);

              const noticePeriod =
                formData.dateOfResignation && releavingDate
                  ? calculateNoticePeriod(
                      formData.dateOfResignation,
                      releavingDate,
                    )
                  : "";

              setFormData((prev) => ({
                ...prev,
                dateOfReleaving: releavingDate,
                noticePeriod,
              }));
            }}
            showIcon
            dateFormat="dd/mm/yy"
            placeholder="Select Releaving"
            className="w-full bg-input border border-gray-300 rounded-md"
            inputClassName="w-full px-3 py-1.5 bg-input shadow-none focus:outline-none"
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label className="px-3">Notice Period (Days)</Label>
          <InputText
            value={`${formData.noticePeriod} Days`}
            readOnly
            placeholder="Auto calculated"
            className="w-full px-3 py-1.5 border border-gray-300 rounded-md bg-input shadow-none focus:outline-none"
          />
        </div>
      </div>
    </section>
  );
}

export default Employment;
